import { TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { NutritionProgram } from "@corpuscomplete/core/nutrition/program";
import { BodyMetricGoal } from "@corpuscomplete/core/body/body-metric-goal";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@corpuscomplete/web-ui/components/empty";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import {
  AlertTriangleIcon,
  ClipboardListIcon,
  MoveRight,
  PlusIcon,
  TargetIcon,
} from "lucide-react";
import { useState } from "react";
import { NutrientTargetsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { queries } from "@corpuscomplete/core/zero";
import { ProgramWizardDialog } from "../components/program-wizard";

/**
 * Programs route page for active nutrition program status and creation.
 * Contract: displays query-backed state and hosts the
 * NutritionProgramWizard provider handoff for create-program flows.
 */
export function ProgramsPage() {
  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text className="mr-auto">Programs</Text>
        </PageHeader>
        <PageContent>
          <CurrentProgram />
        </PageContent>
      </PageBody>
    </Page>
  );
}

function CurrentProgram() {
  const { userId } = useAuth();
  const today = Temporal.Now.plainDateISO().toString();
  const [program, programDetails] = useQuery(
    queries.nutritionProgram.onDate({ userId, date: today }),
  );
  const [weightGoal, weightGoalDetails] = useQuery(
    queries.bodyMetricGoal.forTypeOnDate({ userId, type: "weight", date: today }),
  );
  const [newProgramOpen, setNewProgramOpen] = useState(false);

  if (programDetails.type === "unknown") {
    return null;
  }
  if (!program) {
    return (
      <>
        <Empty className="flex-1">
          <EmptyHeader>
            <EmptyMedia>
              <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                <ClipboardListIcon className="size-8 text-muted-foreground" />
              </div>
            </EmptyMedia>
            <EmptyTitle>No nutrition program</EmptyTitle>
            <EmptyDescription>
              Create a nutrition program to set your daily calorie and macronutrient targets.
            </EmptyDescription>
          </EmptyHeader>
          <Button onClick={() => setNewProgramOpen(true)}>
            <PlusIcon className="mr-1.5 size-4" />
            Create Program
          </Button>
        </Empty>
        <ProgramWizardDialog open={newProgramOpen} onClose={() => setNewProgramOpen(false)} />
      </>
    );
  }
  const programToday = NutritionProgram.dayForDate([program], Temporal.Now.plainDateISO());

  return (
    <div className="flex flex-col gap-4">
      <Text variant="h4">Current Program</Text>
      <div className="flex flex-row items-center gap-2">
        <Text variant="sm">{TemporalHelpers.formatDate(program.dateStart, "monthDayYear")}</Text>
        <MoveRight className="size-4" />
        <Text variant="sm">Now</Text>
      </div>
      {programToday && (
        <div className="flex flex-row items-center gap-2">
          <Text variant="sm">Today's Targets:</Text>
          <NutrientTargetsLabel targets={programToday.nutrientTargets} />
        </div>
      )}
      {weightGoalDetails.type !== "unknown" && (
        <>
          {weightGoal && <WeightGoalCard goal={weightGoal} />}

          <GoalMismatchWarning
            programGoalId={program.expenditure?.goalId}
            activeGoalId={weightGoal?.id}
          />
        </>
      )}

      <Button onClick={() => setNewProgramOpen(true)}>
        <Text variant="sm">New Program</Text>
      </Button>

      <ProgramWizardDialog open={newProgramOpen} onClose={() => setNewProgramOpen(false)} />
    </div>
  );
}

function GoalMismatchWarning({
  programGoalId,
  activeGoalId,
}: {
  programGoalId: string | null | undefined;
  activeGoalId: string | undefined;
}) {
  if (programGoalId === activeGoalId) {
    return null;
  }
  if (programGoalId === null || programGoalId === undefined) {
    if (activeGoalId === undefined) {
      return null;
    }
    return (
      <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
        <AlertTriangleIcon className="size-4 shrink-0" />
        <Text variant="sm">
          You have an active goal that isn't linked to your nutrition program.
        </Text>
      </div>
    );
  }
  if (activeGoalId === undefined) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
        <AlertTriangleIcon className="size-4 shrink-0" />
        <Text variant="sm">Your nutrition program references a goal that is no longer active.</Text>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
      <AlertTriangleIcon className="size-4 shrink-0" />
      <Text variant="sm">
        Your current goal doesn't match the one used by your nutrition program.
      </Text>
    </div>
  );
}

function WeightGoalCard({ goal }: { goal: BodyMetricGoal.Info }) {
  const typedGoal = BodyMetricGoal.Weight.fromGoal(goal);
  if (!typedGoal || typedGoal.targets.weight.target.type !== "exact") {
    return null;
  }
  const { targets, dateTarget } = typedGoal;

  return (
    <div className="flex flex-col gap-2 rounded-md bg-muted p-3">
      <div className="flex items-center gap-2">
        <TargetIcon className="size-4 text-muted-foreground" />
        <Text variant="sm" className="font-medium">
          Weight Goal
        </Text>
      </div>
      <div className="flex justify-between">
        <Text variant="sm" className="text-muted-foreground">
          Target weight:
        </Text>
        <Text variant="sm" className="font-medium">
          {targets.weight.target.value} {targets.weight.unitKey}
        </Text>
      </div>
      {dateTarget && (
        <div className="flex justify-between">
          <Text variant="sm" className="text-muted-foreground">
            Target date:
          </Text>
          <Text variant="sm" className="font-medium">
            {TemporalHelpers.formatDate(dateTarget, "monthDayYear")}
          </Text>
        </div>
      )}
    </div>
  );
}
